/** Loomua route composition through the shared responsive application shell. */
import { lazy, Suspense, useEffect, useState } from "react";
import type { HealthResponse } from "@loomua/contracts";
import { AppShell } from "./AppShell";
import {
  consumeSocialAuthReturnPath,
  exchangeEmailConfirmationCodeForSession,
  getBrowserSession,
  matchesEmailConfirmationSession,
  navigateBrowser,
  readEmailConfirmationCode,
  readEmailConfirmationCallback,
  readPasswordRecoveryCallback,
  readSocialAuthCallbackError,
  safeAuthReturnPath,
  signOutBrowserSession,
  subscribeToBrowserSession
} from "./auth";
import { getHealth } from "./health";
import { InterfaceLanguageProvider } from "./interfaceLanguage";
import { HomePage } from "./HomePage";
import { MotionRoute } from "./motionComponents";
import type { Viewer } from "./routeAccess";
import {
  AuthWall,
  EmailConfirmationResult,
  PasswordRecoveryRequest,
  RecoveredPasswordForm,
  SharedState
} from "./ui";

const ReleaseReaderPage = lazy(() =>
  import("./ReleaseReaderPage").then((module) => ({
    default: module.ReleaseReaderPage
  }))
);
const PublicReaderPage = lazy(() =>
  import("./PublicReaderPage").then((module) => ({
    default: module.PublicReaderPage
  }))
);
const FandomHubPage = lazy(() =>
  import("./FandomHubPage").then((module) => ({
    default: module.FandomHubPage
  }))
);
const AccountPage = lazy(() =>
  import("./AccountPage").then((module) => ({
    default: module.AccountPage
  }))
);
const PublishStudio = lazy(() =>
  import("./PublishStudio").then((module) => ({
    default: module.PublishStudio
  }))
);
const SearchPage = lazy(() =>
  import("./SearchPage").then((module) => ({
    default: module.SearchPage
  }))
);
const BookshelfPage = lazy(() =>
  import("./BookshelfPage").then((module) => ({
    default: module.BookshelfPage
  }))
);

type BrowserLocation = {
  pathname: string;
  search: string;
  hash: string;
};

type ViewTransitionDocument = Document & {
  startViewTransition?: (callback: () => void) => { finished: Promise<void> };
};

function readBrowserLocation(): BrowserLocation {
  return {
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash
  };
}

function routeKey(location: BrowserLocation) {
  return `${location.pathname}${location.search}`;
}

function isEmailConfirmationRoute(location: BrowserLocation) {
  return (
    location.pathname === "/" &&
    new URLSearchParams(location.search).get("authCallback") === "signup"
  );
}

function prefersReducedMotion() {
  return (
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false
  );
}

function shouldHandleNavigationClick(event: MouseEvent) {
  if (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.altKey ||
    event.ctrlKey ||
    event.shiftKey
  ) {
    return undefined;
  }
  const target = event.target;
  if (!(target instanceof Element)) return undefined;
  const anchor = target.closest<HTMLAnchorElement>("a[href]");
  if (
    !anchor ||
    anchor.target ||
    anchor.hasAttribute("download") ||
    anchor.dataset.nativeNavigation === "true"
  ) {
    return undefined;
  }
  const nextUrl = new URL(anchor.href, window.location.href);
  if (nextUrl.origin !== window.location.origin) return undefined;
  if (
    nextUrl.pathname === window.location.pathname &&
    nextUrl.search === window.location.search
  ) {
    return undefined;
  }
  return nextUrl;
}

function shouldHandleNavigationSubmit(event: SubmitEvent) {
  if (event.defaultPrevented) return undefined;
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return undefined;
  if (
    form.method.toLowerCase() !== "get" ||
    form.target ||
    form.dataset.nativeNavigation === "true"
  ) {
    return undefined;
  }
  const nextUrl = new URL(form.action, window.location.href);
  if (nextUrl.origin !== window.location.origin) return undefined;
  const search = new URLSearchParams();
  for (const [key, value] of new FormData(form)) {
    if (typeof value === "string" && value) search.append(key, value);
  }
  nextUrl.search = search.toString();
  return nextUrl;
}

export function App() {
  const [location, setLocation] = useState(readBrowserLocation);
  const fandomMatch = /^\/fandoms\/([^/]+)$/.exec(location.pathname);
  const releaseReaderMatch = /^\/works\/([^/]+)\/read$/.exec(location.pathname);
  const publicReaderMatch = /^\/public\/works\/([^/]+)$/.exec(
    location.pathname
  );
  const authReturnTo =
    new URLSearchParams(location.search).get("returnTo") ?? "/";
  const socialAuthCallbackError = readSocialAuthCallbackError(
    location.search,
    location.hash
  );
  const [emailConfirmationLocation] = useState(() => ({
    search: window.location.search,
    hash: window.location.hash
  }));
  const emailConfirmationState = readEmailConfirmationCallback(
    emailConfirmationLocation.search,
    emailConfirmationLocation.hash
  );
  const emailConfirmationRouteActive = isEmailConfirmationRoute(location);
  const [passwordRecoveryState, setPasswordRecoveryState] = useState(
    readPasswordRecoveryCallback(window.location.search, window.location.hash)
  );
  const [health, setHealth] = useState<HealthResponse>();
  const [unavailable, setUnavailable] = useState(false);
  const [viewer, setViewer] = useState<Viewer>({ status: "loading" });
  const [emailConfirmationSucceeded, setEmailConfirmationSucceeded] =
    useState(false);
  const [emailConfirmationFailed, setEmailConfirmationFailed] = useState(false);

  useEffect(() => {
    const updateLocation = () => setLocation(readBrowserLocation());
    const navigateWithinApp = (nextUrl: URL) => {
      const applyNavigation = () => {
        window.history.pushState(
          {},
          "",
          `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`
        );
        updateLocation();
      };
      if (!prefersReducedMotion()) {
        const transition = (document as ViewTransitionDocument)
          .startViewTransition;
        if (typeof transition === "function") {
          transition.call(document, applyNavigation);
        } else {
          applyNavigation();
        }
      } else {
        applyNavigation();
      }
      if (!nextUrl.hash) {
        window.requestAnimationFrame(() =>
          window.scrollTo({ top: 0, left: 0 })
        );
      }
    };
    const handleClick = (event: MouseEvent) => {
      const nextUrl = shouldHandleNavigationClick(event);
      if (!nextUrl) return;
      event.preventDefault();
      navigateWithinApp(nextUrl);
    };
    const handleSubmit = (event: SubmitEvent) => {
      const nextUrl = shouldHandleNavigationSubmit(event);
      if (!nextUrl) return;
      event.preventDefault();
      navigateWithinApp(nextUrl);
    };
    window.addEventListener("popstate", updateLocation);
    document.addEventListener("click", handleClick);
    document.addEventListener("submit", handleSubmit);
    return () => {
      window.removeEventListener("popstate", updateLocation);
      document.removeEventListener("click", handleClick);
      document.removeEventListener("submit", handleSubmit);
    };
  }, []);

  useEffect(() => {
    void getHealth()
      .then(setHealth)
      .catch(() => setUnavailable(true));
    const updateViewer = (
      session: Awaited<ReturnType<typeof getBrowserSession>>,
      authEvent?: string
    ) => {
      const isPasswordRecovery =
        passwordRecoveryState === "ready" || authEvent === "PASSWORD_RECOVERY";
      const currentAuthReturnTo =
        new URLSearchParams(window.location.search).get("returnTo") ?? "/";
      if (authEvent === "PASSWORD_RECOVERY") {
        setPasswordRecoveryState("ready");
      }
      if (
        emailConfirmationState === "pending" &&
        authEvent === "SIGNED_IN" &&
        session &&
        matchesEmailConfirmationSession(
          emailConfirmationLocation.search,
          emailConfirmationLocation.hash,
          session.accessToken
        )
      ) {
        setEmailConfirmationSucceeded(true);
      }
      if (session && !isPasswordRecovery) {
        if (window.location.pathname === "/register") {
          navigateBrowser(safeAuthReturnPath(currentAuthReturnTo));
          return;
        }
        const returnPath = consumeSocialAuthReturnPath(window.sessionStorage);
        const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        if (returnPath && returnPath !== currentPath) {
          window.location.assign(returnPath);
          return;
        }
      } else if (socialAuthCallbackError) {
        consumeSocialAuthReturnPath(window.sessionStorage);
      }
      setViewer(
        session
          ? { status: "authenticated", userId: session.userId }
          : { status: "anonymous" }
      );
    };
    const unsubscribe = subscribeToBrowserSession(updateViewer);
    const resetViewer = () => setViewer({ status: "anonymous" });
    void getBrowserSession()
      .then((session) => updateViewer(session))
      .catch(resetViewer);
    let registrationActivationChecked = false;
    const recheckRegistration = () => {
      if (
        window.location.pathname !== "/register" ||
        registrationActivationChecked
      ) {
        return;
      }
      registrationActivationChecked = true;
      void getBrowserSession()
        .then((session) => updateViewer(session))
        .catch(resetViewer);
    };
    const recheckVisibleRegistration = () => {
      if (document.visibilityState === "visible") {
        recheckRegistration();
      } else {
        registrationActivationChecked = false;
      }
    };
    const resetRegistrationActivation = () => {
      registrationActivationChecked = false;
    };
    window.addEventListener("focus", recheckRegistration);
    window.addEventListener("blur", resetRegistrationActivation);
    document.addEventListener("visibilitychange", recheckVisibleRegistration);
    return () => {
      unsubscribe();
      window.removeEventListener("focus", recheckRegistration);
      window.removeEventListener("blur", resetRegistrationActivation);
      document.removeEventListener(
        "visibilitychange",
        recheckVisibleRegistration
      );
    };
  }, []);

  useEffect(() => {
    const confirmationCode = readEmailConfirmationCode(
      emailConfirmationLocation.search,
      emailConfirmationLocation.hash
    );
    if (!confirmationCode) return;

    let cancelled = false;
    void exchangeEmailConfirmationCodeForSession(confirmationCode)
      .then((session) => {
        if (cancelled) return;
        window.history.replaceState({}, "", "/?authCallback=signup");
        setLocation(readBrowserLocation());
        setViewer({ status: "authenticated", userId: session.userId });
        setEmailConfirmationSucceeded(true);
      })
      .catch(() => {
        if (cancelled) return;
        setEmailConfirmationFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  async function checkRegistrationSession(): Promise<boolean> {
    let session;
    try {
      session = await getBrowserSession();
    } catch {
      setViewer({ status: "anonymous" });
      return false;
    }
    if (!session) return false;
    navigateBrowser(safeAuthReturnPath(authReturnTo));
    return true;
  }

  async function signOut() {
    await signOutBrowserSession();
    setViewer({ status: "anonymous" });
  }

  let routeContent;
  if (location.pathname === "/forgot-password") {
    routeContent = <PasswordRecoveryRequest />;
  } else if (passwordRecoveryState) {
    routeContent = (
      <RecoveredPasswordForm recoveryState={passwordRecoveryState} />
    );
  } else if (
    emailConfirmationRouteActive &&
    (emailConfirmationState === "error" || emailConfirmationFailed)
  ) {
    routeContent = <EmailConfirmationResult state="error" />;
  } else if (
    emailConfirmationRouteActive &&
    emailConfirmationSucceeded &&
    viewer.status === "authenticated"
  ) {
    routeContent = <EmailConfirmationResult state="success" />;
  } else if (publicReaderMatch) {
    routeContent = <PublicReaderPage workId={publicReaderMatch[1]!} />;
  } else if (releaseReaderMatch) {
    routeContent =
      viewer.status === "loading" ? (
        <SharedState state="loading" />
      ) : viewer.status === "anonymous" ? (
        <AuthWall />
      ) : (
        <ReleaseReaderPage workId={releaseReaderMatch[1]!} />
      );
  } else if (fandomMatch) {
    routeContent =
      viewer.status === "loading" ? (
        <SharedState state="loading" />
      ) : viewer.status === "anonymous" ? (
        <AuthWall />
      ) : (
        <FandomHubPage fandomId={fandomMatch[1]!} />
      );
  } else if (location.pathname === "/create") {
    routeContent =
      viewer.status === "loading" ? (
        <SharedState state="loading" />
      ) : viewer.status === "anonymous" ? (
        <AuthWall />
      ) : (
        <PublishStudio />
      );
  } else if (location.pathname === "/bookmarks") {
    routeContent =
      viewer.status === "loading" ? (
        <SharedState state="loading" />
      ) : viewer.status === "anonymous" ? (
        <AuthWall />
      ) : (
        <BookshelfPage />
      );
  } else if (location.pathname === "/search") {
    const query = new URLSearchParams(location.search).get("q")?.trim() ?? "";
    routeContent =
      viewer.status === "loading" ? (
        <SharedState state="loading" />
      ) : viewer.status === "anonymous" ? (
        <AuthWall returnTo={location.pathname + location.search} />
      ) : (
        <SearchPage query={query} />
      );
  } else if (socialAuthCallbackError) {
    routeContent = <AuthWall />;
  } else if (location.pathname === "/login") {
    routeContent =
      viewer.status === "loading" ? (
        <SharedState state="loading" />
      ) : (
        <AuthWall initialMode="sign-in" returnTo={authReturnTo} />
      );
  } else if (location.pathname === "/register") {
    routeContent =
      viewer.status === "loading" ? (
        <SharedState state="loading" />
      ) : (
        <AuthWall
          initialMode="register"
          returnTo={authReturnTo}
          onCheckSession={checkRegistrationSession}
        />
      );
  } else if (
    location.pathname === "/profile" ||
    location.pathname === "/account"
  ) {
    routeContent =
      viewer.status === "loading" ? (
        <SharedState state="loading" />
      ) : viewer.status === "anonymous" ? (
        <AuthWall />
      ) : (
        <AccountPage />
      );
  } else if (location.pathname !== "/") {
    routeContent = <SharedState state="not-found" />;
  } else {
    routeContent = (
      <>
        <span className="visually-hidden" aria-live="polite">
          {health
            ? `Connected to ${health.environment}`
            : unavailable
              ? "API unavailable"
              : "Connecting to the Loomua API…"}
        </span>
        {viewer.status === "loading" ? (
          <SharedState state="loading" />
        ) : viewer.status === "anonymous" ? (
          <HomePage mode="public" />
        ) : (
          <HomePage />
        )}
      </>
    );
  }

  return (
    <InterfaceLanguageProvider>
      <AppShell session={viewer} onSignOut={signOut}>
        <MotionRoute routeKey={routeKey(location)}>
          <Suspense fallback={<SharedState state="loading" />}>
            {routeContent}
          </Suspense>
        </MotionRoute>
      </AppShell>
    </InterfaceLanguageProvider>
  );
}
